import { useMemo } from "react";
import { Image as ImageIcon, FileText, X } from "lucide-react";

/* Read one or more files as data URLs. Returns Promise<Attachment[]>.
   Attachment shape: { name, type, dataUrl } — matches what AttachmentPill expects. */
export function readFilesAsAttachments(fileList) {
  const files = Array.from(fileList || []);
  return Promise.all(
    files.map(
      (f) =>
        new Promise((resolve) => {
          const r = new FileReader();
          r.onload = () => resolve({ name: f.name, type: f.type, dataUrl: r.result });
          r.onerror = () => resolve(null);
          r.readAsDataURL(f);
        })
    )
  ).then((arr) => arr.filter(Boolean));
}

/* Lightweight inline upload control. Renders a clickable label that opens the
   native file picker; calls onPick with an array of attachments (or single
   when multiple={false}). Used in the SalesPerson wizard and elsewhere. */
export function FileAttachButton({
  label,
  accept,
  multiple = true,
  onPick,
  // `raw` hands back native File objects instead of base64 attachments.
  // Callers that upload to storage need the File — base64 in a JSON body is
  // ~33% larger, and a 10MB PDF becomes ~13MB, exceeding any sane limit.
  // Defaults to false so existing callers are unaffected.
  raw = false,
  className = "",
}) {
  const id = useMemo(() => "att_" + Math.random().toString(36).slice(2, 9), []);
  return (
    <label
      htmlFor={id}
      className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg ring-1 ring-inset ring-slate-300 bg-white text-slate-700 hover:bg-slate-50 cursor-pointer transition-colors ${className}`}
    >
      <ImageIcon className="w-3.5 h-3.5" />
      {label}
      <input
        id={id}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={async (e) => {
          if (raw) {
            const files = Array.from(e.target.files || []);
            if (files.length) onPick(multiple ? files : files[0]);
            e.target.value = "";
            return;
          }
          const att = await readFilesAsAttachments(e.target.files);
          if (att.length) onPick(multiple ? att : att[0]);
          e.target.value = "";
        }}
      />
    </label>
  );
}

/* Tiny preview pill for an attachment (image thumbnail or doc filename).
   The image / icon is clickable for view, with an optional remove × button. */
export function AttachmentPill({ a, onRemove, onView }) {
  const isImg = a.type?.startsWith("image/");
  // Falls back to `url` for remotely stored attachments — documents uploaded
  // to R2 carry { name, type, url } rather than a dataUrl.
  const src = a.dataUrl || a.url;
  return (
    <span className="inline-flex items-center gap-1.5 bg-slate-100 rounded-md pl-1 pr-1.5 py-1 text-xs">
      {isImg ? (
        <img
          src={src}
          alt={a.name}
          className="w-6 h-6 rounded object-cover cursor-pointer"
          onClick={onView}
        />
      ) : (
        <span
          className="w-6 h-6 rounded bg-white grid place-items-center text-slate-400 cursor-pointer"
          onClick={onView}
        >
          <FileText className="w-3.5 h-3.5" />
        </span>
      )}
      <span className="font-medium text-slate-700 max-w-[120px] truncate">{a.name}</span>
      {onRemove && (
        <button onClick={onRemove} className="text-slate-400 hover:text-red-600">
          <X className="w-3 h-3" />
        </button>
      )}
    </span>
  );
}
